/**
 * Client-Side Token Counting Utility
 *
 * Browser-compatible token counting functions
 */

export interface TokenCountResult {
  tokens: number;
  words: number;
  characters: number;
  model: string;
}

// Model-specific token-to-word ratios (approximations)
const MODEL_CONFIGS: Record<string, number> = {
  "gpt-4": 0.75,
  "gpt-4-turbo": 0.75,
  "gpt-3.5-turbo": 0.8,
  "claude-3-opus": 0.7,
  "claude-3-sonnet": 0.7,
  "claude-3-haiku": 0.7,
  "gemini-pro": 0.8,
  default: 0.75,
};

/**
 * Count tokens in text using word-based approximation
 */
export function countTokens(
  text: string,
  model: string = "gpt-4",
): TokenCountResult {
  if (!text || text.trim().length === 0) {
    return {
      tokens: 0,
      words: 0,
      characters: 0,
      model,
    };
  }

  // Get model config
  const ratio = MODEL_CONFIGS[model] ?? MODEL_CONFIGS.default ?? 0.75;

  // Count words (split by whitespace and filter empty strings)
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  // Count characters
  const characters = text.length;

  // Calculate tokens
  const tokens = Math.ceil(words * ratio);

  return {
    tokens,
    words,
    characters,
    model,
  };
}

/**
 * Count tokens for a single message
 */
export function countTokensForMessage(
  content: string,
  role: string = "user",
  model: string = "gpt-4",
): TokenCountResult {
  const text = `${role}: ${content}`;
  return countTokens(text, model);
}

// Example usage:
/*
const result = countTokens("Hello, how are you?", "gpt-4")
console.log(result) // { tokens: 4, words: 4, characters: 18, model: "gpt-4" }

const messages = [
  { role: "user", content: "Hello" },
  { role: "assistant", content: "Hi there!" }
]
const chatResult = countTokensForMessages(messages, "gpt-4")
console.log(chatResult) // Combined token count for all messages
*/
